'use client'

import { motion, useReducedMotion } from 'framer-motion'
import { useCallback, useEffect, useState } from 'react'
import {
  ChevronLeftIcon,
  ChevronRightIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline'
import { Modal } from '@/Modal'

const revealEase = [0.22, 1, 0.36, 1] as const
const STAGGER_S = 0.14
const FADE_DURATION = 0.55

export type RetreatLightboxSlide = {
  src: string
  alt: string
  /** Modal image; defaults to a larger Gumlet URL derived from `src` */
  fullscreenSrc?: string
}

function fullscreenFromThumb(url: string): string {
  try {
    const u = new URL(url)
    if (u.hostname.includes('gumlet.io')) {
      u.searchParams.set('w', '1920')
      u.searchParams.set('dpr', '2')
    }
    return u.toString()
  } catch {
    return url
  }
}

type RetreatLightboxImagesProps = {
  slides: RetreatLightboxSlide[]
  gridClassName: string
  imgClassName: string
  itemClassName?: string
  /** Wrap each tile in `<figure>` (e.g. Amazon add-on cards) */
  asFigure?: boolean
  /**
   * Responsive `sizes` for thumbnails (below the fold). Helps the browser pick
   * Gumlet/`src` resolution and avoids over-fetching on mobile.
   */
  thumbSizes?: string
}

export function RetreatLightboxImages({
  slides,
  gridClassName,
  imgClassName,
  itemClassName,
  asFigure = false,
  thumbSizes = '(max-width: 640px) 100vw, (max-width: 1024px) 50vw, 400px',
}: RetreatLightboxImagesProps) {
  const reduceMotion = useReducedMotion()
  const [open, setOpen] = useState(false)
  const [index, setIndex] = useState(0)

  const len = slides.length

  const goNext = useCallback(() => {
    setIndex(i => (i + 1) % len)
  }, [len])

  const goPrev = useCallback(() => {
    setIndex(i => (i - 1 + len) % len)
  }, [len])

  useEffect(() => {
    if (!open || len <= 1) return
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'ArrowLeft') goPrev()
      else if (e.key === 'ArrowRight') goNext()
    }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [open, len, goNext, goPrev])

  useEffect(() => {
    if (!open) return
    const prev = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    return () => {
      document.body.style.overflow = prev
    }
  }, [open])

  if (!len) return null

  const openAt = (i: number) => {
    setIndex(i)
    setOpen(true)
  }

  const close = () => setOpen(false)

  const current = slides[index]
  const fullSrc =
    current?.fullscreenSrc ?? (current ? fullscreenFromThumb(current.src) : '')

  const itemMotionProps = (i: number) => ({
    initial: { opacity: 0, y: 16 } as const,
    whileInView: { opacity: 1, y: 0 } as const,
    viewport: { once: true, amount: 0.22, margin: '0px 0px -12% 0px' } as const,
    transition: {
      duration: FADE_DURATION,
      ease: revealEase,
      delay: i * STAGGER_S,
    },
  })

  return (
    <>
      <div className={gridClassName}>
        {slides.map((slide, i) => {
          const button = (
            <button
              type="button"
              onClick={() => openAt(i)}
              className="block h-full w-full cursor-zoom-in rounded-[inherit] border-0 bg-transparent p-0 text-left focus:outline-none focus-visible:ring-2 focus-visible:ring-[#b94d3c] focus-visible:ring-offset-2"
              aria-label={`Enlarge: ${slide.alt}`}
            >
              <img
                src={slide.src}
                alt={slide.alt}
                className={imgClassName}
                loading="lazy"
                decoding="async"
                sizes={thumbSizes}
                fetchPriority="low"
              />
            </button>
          )

          const key = `${slide.src}-${i}`
          const cls = itemClassName

          if (reduceMotion) {
            if (asFigure) {
              return (
                <figure key={key} className={cls}>
                  {button}
                </figure>
              )
            }
            return (
              <div key={key} className={cls}>
                {button}
              </div>
            )
          }

          if (asFigure) {
            return (
              <motion.figure
                key={key}
                className={cls}
                {...itemMotionProps(i)}
              >
                {button}
              </motion.figure>
            )
          }

          return (
            <motion.div key={key} className={cls} {...itemMotionProps(i)}>
              {button}
            </motion.div>
          )
        })}
      </div>

      <Modal
        isOpen={open}
        onClose={close}
        maxWidth="6xl"
        showCloseButton={false}
        backdropOpacity="subtle"
        zIndex={100}
        className="!max-w-[min(96rem,calc(100vw-1.5rem))] border border-white/10 !bg-neutral-950 p-0 shadow-2xl ring-0"
      >
        <div className="relative px-4 pb-14 pt-12 sm:px-8 sm:pb-16 sm:pt-14">
          <button
            type="button"
            onClick={close}
            className="absolute right-2 top-2 z-10 flex min-h-11 min-w-11 items-center justify-center rounded-full bg-white/10 text-white transition hover:bg-white/20 focus:outline-none focus-visible:ring-2 focus-visible:ring-white/80 sm:right-5 sm:top-5 sm:min-h-0 sm:min-w-0 sm:p-2.5"
            aria-label="Close"
          >
            <XMarkIcon className="size-6" />
          </button>

          {current && (
            <div className="relative mx-auto aspect-video w-full max-w-[min(100%,calc(min(82vh,900px)*16/9))]">
              <img
                src={fullSrc}
                alt={current.alt}
                className="size-full object-contain"
                fetchPriority="high"
                decoding="async"
              />

              {len > 1 && (
                <>
                  <button
                    type="button"
                    onClick={e => {
                      e.stopPropagation()
                      goPrev()
                    }}
                    className="absolute left-1 top-1/2 z-10 flex min-h-11 min-w-11 -translate-y-1/2 items-center justify-center rounded-full bg-white/10 p-2 text-white transition hover:bg-white/20 focus:outline-none focus-visible:ring-2 focus-visible:ring-white/80 sm:left-2 sm:min-h-0 sm:min-w-0 sm:p-3"
                    aria-label="Previous image"
                  >
                    <ChevronLeftIcon className="size-6 sm:size-7" />
                  </button>
                  <button
                    type="button"
                    onClick={e => {
                      e.stopPropagation()
                      goNext()
                    }}
                    className="absolute right-1 top-1/2 z-10 flex min-h-11 min-w-11 -translate-y-1/2 items-center justify-center rounded-full bg-white/10 p-2 text-white transition hover:bg-white/20 focus:outline-none focus-visible:ring-2 focus-visible:ring-white/80 sm:right-2 sm:min-h-0 sm:min-w-0 sm:p-3"
                    aria-label="Next image"
                  >
                    <ChevronRightIcon className="size-6 sm:size-7" />
                  </button>
                  <div className="absolute bottom-2 left-1/2 z-10 -translate-x-1/2 rounded-full bg-white/10 px-4 py-1.5 text-sm text-white/90 sm:bottom-3">
                    {index + 1} / {len}
                  </div>
                </>
              )}
            </div>
          )}
        </div>
      </Modal>
    </>
  )
}
